/**
 * O cliente do `comunicacao` — o número (8.1), a conversa (8.3), a mídia (8.4) e o e-mail (8.5).
 *
 * O módulo espelha o serviço, um a um. **Nenhum tipo de contrato é digitado aqui** (AD-16): todos
 * são apelidos dos schemas gerados em `src/api/tipos-gerados/comunicacao.ts`. Alterou o contrato do
 * serviço, rode `npm run gerar-tipos` — o CI reprova a divergência.
 *
 * **As três funções do número não recebem id de dono**, e a ausência é a metade do FR-71 que cabe
 * ao SPA: o sujeito é sempre quem está autenticado, lido das claims do lado do serviço. Não há como
 * escrever, deste arquivo, uma chamada que peça o número de outra pessoa.
 *
 * **As três da conversa recebem o id da conversa, e nada mais.** Quem recorta o que cada um enxerga
 * é a RLS do serviço, linha a linha (FR-39): a lista vem com o que a policy devolveu, e um `id` que
 * ela esconde responde `404` — não há filtro a escrever aqui, e escrevê-lo seria uma segunda cópia
 * da regra, na camada que menos consegue garanti-la.
 *
 * **As mutações invalidam, e não remendam o cache com a resposta.** É o mesmo cuidado das outras
 * mutações do repositório: o serviço tem ramos idempotentes e regras próprias (o `409` de número
 * desconectado, o `403` de número alheio), e a releitura é o que mantém a tela mostrando o que o
 * serviço tem, e não o que ela supôs.
 *
 * As funções são chamadas autenticadas comuns: nenhuma passa `semRenovacao`, porque um acesso
 * expirado enquanto alguém espera o QR — ou digita uma mensagem — deve se resolver sozinho, sem
 * jogar a pessoa de volta para a tela de login no meio do trabalho.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chamar } from '@/api/cliente'
import type { components } from '@/api/tipos-gerados/comunicacao'

/** Os schemas do `openapi.json` do `comunicacao`, gerados por `npm run gerar-tipos` (AD-16). */
type Esquemas = components['schemas']

/**
 * A situação do próprio número, como o serviço a devolve.
 *
 * **Sem token, e não há de onde**: a credencial da instância não existe no banco (AD-9) — o
 * adaptador do serviço a resolve a cada chamada e a descarta. `numero` é nulo enquanto ninguém
 * conectou e **continua preenchido** depois de uma queda: é ele que diz a quem vai reconectar qual
 * chip estava ali.
 */
export type MeuNumero = Esquemas['RespostaDoMeuNumero']

/**
 * O mesmo estado **mais** o QR desta tentativa, como `POST /meu-numero/conectar` o devolve.
 *
 * `codigo_qr` é o conteúdo que vira imagem no navegador — ele gira em segundos e vale para uma
 * leitura —, e `expira_em` existe para a tela poder dizer "peça outro" em vez de deixar a pessoa
 * mirando um quadrado morto.
 */
export type PareamentoDoNumero = Esquemas['RespostaDoPareamento']

/** Os três estados possíveis, como o serviço os declara — a lista não é digitada aqui. */
export type SituacaoDoNumero = Esquemas['SituacaoDoNumero']

/**
 * Chave de cache do próprio número — própria, e não compartilhada com nada.
 *
 * A chave é uma constante nomeada, e não um literal no `useQuery`, pela mesma razão de
 * `CHAVE_DA_CONFIGURACAO_DO_ERP`: quem invalida precisa nomeá-la, e um array escrito duas vezes é
 * como as duas cópias divergem.
 *
 * **Ela não leva o `usuario_id` dentro**, ao contrário de `chaveDosOrcamentos`: o recurso é sempre
 * o de quem está autenticado, e o cache do SPA morre com a sessão — trocar de usuário recarrega a
 * página. Um id na chave sugeriria que existe um segundo número a consultar, que é justamente o que
 * esta história recusa.
 */
export const CHAVE_DO_MEU_NUMERO = ['comunicacao', 'meu-numero'] as const

/**
 * De quanto em quanto tempo a tela relê **enquanto o QR está vivo na tela**, em milissegundos.
 *
 * **Este foi o primeiro `refetchInterval` do repositório, e ele precisa da justificativa escrita.**
 * O CRM recusa relógios por padrão — `criarClienteDeConsultas` desliga até o `refetchOnWindowFocus`
 * —, porque tela que se atualiza sozinha esconde de quem olha *quando* o dado mudou. Aqui a
 * exceção é do desenho do pareamento: quem confirma a conexão **não** é o clique da pessoa, e sim o
 * **webhook** do provedor, que chega ao serviço por fora. Sem a releitura, o Vendedor leria o QR
 * com o celular, o número ficaria conectado no banco, e a tela continuaria dizendo "Pareando" até
 * ele recarregar a página sozinho — e ninguém recarrega uma página que parece estar trabalhando.
 *
 * **Ele tem hora para acabar, e essa é a metade que o torna aceitável.** Um relógio ligado por
 * `situacao === 'pareando'` e mais nada nunca para: um pareamento que o webhook jamais fecha —
 * porque o aviso se perdeu — deixaria a aba pedindo a cada três segundos até alguém fechá-la, para
 * sempre. Quem desliga é `esperarAte`, o instante em que o QR **desta tentativa** expira: passado
 * ele não há mais nada a esperar, e a tela passa a dizer "peça outro" em vez de continuar
 * perguntando. Quem recarrega a página em `pareando` não tem QR nenhum na tela e, portanto, também
 * não liga relógio nenhum.
 *
 * Três segundos é o passo que faz a confirmação parecer imediata sem transformar uma tela aberta e
 * esquecida num gerador de requisições — o QR do WhatsApp gira em dezenas de segundos, e essa é a
 * escala do que se está esperando.
 *
 * **A regra do repositório não é "nenhum relógio": é "todo relógio precisa de um fim escrito".** A
 * 8.3 traz a segunda exceção, e o fim dela é de outra natureza — veja `INTERVALO_DA_CONVERSA`.
 */
export const INTERVALO_DE_ESPERA_DO_QR = 3_000

/**
 * Lê a situação do próprio número (`200`).
 *
 * Quem nunca pediu pareamento recebe `desconectado` com `numero` nulo, e não um `404`: a ausência
 * de linha **é** o estado desconectado, e um `404` faria a tela ter dois caminhos para desenhar a
 * mesma coisa.
 *
 * **O serviço fala com o uazapi em um caso só**: quando a linha está `pareando` e a janela do QR já
 * venceu, ele pergunta ao provedor e reconcilia — é o conserto do pareamento cujo aviso de webhook
 * se perdeu. Isso é invisível daqui, e de propósito: a resposta é a mesma, e a falha daquela
 * consulta devolve o estado gravado. **Esta rota nunca responde `503`.**
 *
 * `403` para quem teve `conectar_proprio_numero` negada — que é o único caminho por onde alguém
 * perde o próprio número.
 */
export function lerMeuNumero(): Promise<MeuNumero> {
  return chamar<MeuNumero>('comunicacao', '/meu-numero')
}

/**
 * Pede o QR e marca o pareamento (`200`).
 *
 * **Sem corpo**: não há nada a escolher. O número não é digitado — quem o informa é o WhatsApp,
 * quando o chip atende — e o dono é sempre quem chamou.
 *
 * A resposta vem com `situacao: 'pareando'`, e **não** `conectado`: quem confirma é o webhook,
 * depois de o QR ser lido. Repetir é legítimo e não é idempotente — o QR gira, e pedir outro é o
 * que se faz quando o anterior expira.
 *
 * `503` `whatsapp_indisponivel` quando o uazapi não responde **ou** quando o ambiente ainda não tem
 * a credencial dele — e nesse caso antes de qualquer socket, com a situação inalterada. Mostre a
 * mensagem do serviço como ela veio.
 */
export function conectarMeuNumero(): Promise<PareamentoDoNumero> {
  return chamar<PareamentoDoNumero>('comunicacao', '/meu-numero/conectar', { method: 'POST' })
}

/**
 * Derruba a sessão do próprio número (`200`).
 *
 * **Repetir responde a mesma coisa**, e o ramo idempotente do serviço nem chega a falar com o
 * provedor: um segundo clique nunca vira `503`.
 *
 * O `numero` é **preservado** na resposta: quem vai reconectar precisa ver qual chip estava ali.
 *
 * `503` quando o uazapi não responde — e a situação fica como estava, em vez de o CRM afirmar uma
 * desconexão que o WhatsApp não fez.
 */
export function desconectarMeuNumero(): Promise<MeuNumero> {
  return chamar<MeuNumero>('comunicacao', '/meu-numero/desconectar', { method: 'POST' })
}

/**
 * A situação do próprio número, relida sozinha **enquanto** há um QR vivo esperando ser lido.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada aqui é o `403` de quem teve a
 * permissão negada — que não muda por insistir — e o `401` de sessão expirada, que o cliente já
 * renova sozinho antes de deixar o erro subir.
 *
 * `esperarAte` é o instante em que o QR desta tentativa expira, no formato ISO que o serviço
 * devolve em `expira_em`. Passá-lo é o que dá **fim** ao relógio: `undefined` ou `null` — quem
 * recarregou a página, ou quem já viu o código vencer — desliga a releitura por completo.
 *
 * O `refetchInterval` é uma **função pura do estado da consulta e desse instante**, e não um número
 * fixo nem um contador guardado num `ref`: ele é reavaliado a cada ciclo do observador, e um
 * contador ali seria incrementado um número de vezes que ninguém consegue prever. Duas condições, e
 * as duas precisam valer: a resposta corrente diz `pareando`, e o prazo ainda não passou.
 *
 * A parada acontece **no ciclo seguinte** ao vencimento, e não no milissegundo exato: quem reavalia
 * o intervalo é o próprio observador, depois de cada busca. O atraso é de no máximo um intervalo, e
 * a tela já mostrou "peça outro" antes disso — quem cuida da mensagem é o componente, com um
 * relógio próprio no instante da expiração.
 */
export function useMeuNumero(esperarAte?: string | null) {
  return useQuery({
    queryKey: CHAVE_DO_MEU_NUMERO,
    queryFn: lerMeuNumero,
    retry: false,
    refetchInterval: (consulta) => {
      if (consulta.state.data?.situacao !== 'pareando') {
        return false
      }
      if (esperarAte === undefined || esperarAte === null) {
        return false
      }
      return Date.parse(esperarAte) > Date.now() ? INTERVALO_DE_ESPERA_DO_QR : false
    },
  })
}

/**
 * De quanto em quanto tempo a tela de WhatsApp relê a situação do próprio número (11.12), em ms.
 *
 * A faixa "Desconectado" da tela de conversas precisa sumir quando o número volta — e quem o faz
 * voltar é o pareamento em Integrações, **outra** tela, ou a reconexão do próprio aparelho, que
 * chega por webhook. Trinta segundos é a escala de "o número voltou", e não a de uma conversa.
 *
 * **O fim deste relógio é o foco da aba**, como o da conversa: `refetchIntervalInBackground` fica
 * no default `false`, e a aba em segundo plano não pergunta nada. E `refetchOnWindowFocus` liga
 * aqui: quem volta de Integrações para esta aba vê a situação nova na hora.
 */
export const INTERVALO_DA_SITUACAO_DO_NUMERO = 30_000

/**
 * A situação do próprio número, **relida** enquanto a tela de conversas estiver à vista (11.12).
 *
 * Mesma chave de `useMeuNumero` — é o mesmo recurso, e o cartão de Integrações e a faixa desta
 * tela não podem discordar. Só o relógio muda: aqui ele é o de `INTERVALO_DA_SITUACAO_DO_NUMERO`.
 */
export function useSituacaoDoMeuNumero() {
  return useQuery({
    queryKey: CHAVE_DO_MEU_NUMERO,
    queryFn: lerMeuNumero,
    retry: false,
    refetchInterval: INTERVALO_DA_SITUACAO_DO_NUMERO,
    refetchOnWindowFocus: true,
  })
}

/**
 * Pede o QR e relê a situação.
 *
 * **`onSuccess`, e não `onSettled`**: o desfecho de erro aqui é o `503` do provedor, e ele diz que
 * **nada** mudou no serviço — reler seria uma requisição que já se sabe que devolveria o mesmo
 * corpo. No sucesso a releitura importa: ela é o que liga o relógio de `useMeuNumero`, porque é ela
 * que traz `situacao: 'pareando'` para o cache.
 *
 * O QR em si **não** entra no cache da consulta: ele é o resultado desta mutação, vive na tela
 * enquanto ela estiver montada e morre com ela. Guardá-lo no cache o faria reaparecer, morto, na
 * próxima visita à tela.
 */
export function useConectarMeuNumero() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: conectarMeuNumero,
    onSuccess: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_MEU_NUMERO }),
  })
}

/**
 * Desconecta e relê a situação.
 *
 * **`onSettled`, e não `onSuccess`** — e a diferença é justamente o caso em que a tela está errada.
 * A recusa esperada é o `503` do provedor, que não muda nada; mas o `403` que aparece quando o
 * Admin acabou de retirar a permissão significa que a tela está desenhando ações que quem olha já
 * não tem. Relendo nos dois caminhos, o erro aparece uma vez e o cartão passa a mostrar o que o
 * serviço responde.
 */
export function useDesconectarMeuNumero() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: desconectarMeuNumero,
    onSettled: () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_MEU_NUMERO }),
  })
}

// --- A tela de conversa (8.3, FR-37, FR-38, FR-71, AD-25) -----------------------------------

/**
 * Uma linha da lista de conversas, como o serviço a devolve.
 *
 * **Sem razão social e sem nome de contato**, e a ausência é o AD-25: quem os resolve é o SPA, com o
 * JWT de quem está olhando, chamando o `cadastro` em lote. O que vem daqui é o que o `comunicacao`
 * tem — os três campos da réplica dele (`pessoa_id`, `empresa_id`, `estado_da_empresa`), nulos
 * quando ela ainda não sabe —, mais o número e a prévia da última mensagem.
 */
export type Conversa = Esquemas['RespostaDaConversa']

/** Uma mensagem, como as três rotas de mensagem a devolvem. */
export type Mensagem = Esquemas['RespostaDaMensagem']

/** `enviada` ou `recebida`, como o serviço as declara — a lista não é digitada aqui. */
export type DirecaoDaMensagem = Esquemas['DirecaoDaMensagem']

/**
 * `documento`, `imagem`, `video` ou `audio` — os quatro tipos, como o serviço os declara (8.4).
 *
 * A lista **não** é digitada aqui, pela razão de sempre (AD-16): um tipo novo no serviço apareceria
 * como erro de compilação em todo `switch` que o trata, e não como um caso que a tela ignora.
 */
export type TipoDeMidia = Esquemas['TipoDeMidia']

/**
 * Os campos que o `POST /conversas/{id}/midias` recebe em `multipart/form-data`.
 *
 * `arquivo` é o `File` do `<input type="file">` ou o `Blob` que o `MediaRecorder` produziu; `tipo`
 * é o que decide como o WhatsApp entrega (um áudio mandado como documento chega como anexo). A
 * `legenda` vira o texto da **mesma** mensagem, e nunca uma segunda.
 */
export interface MidiaAEnviar {
  arquivo: Blob
  tipo: TipoDeMidia
  nome?: string
  legenda?: string
  duracaoEmSegundos?: number
}

/**
 * Chave de cache da lista de conversas.
 *
 * Ela **não** leva o `usuario_id` dentro, pela mesma razão de `CHAVE_DO_MEU_NUMERO`: o recurso é
 * sempre o de quem está autenticado (a RLS recorta do lado do serviço), e o cache do SPA morre com
 * a sessão.
 */
export const CHAVE_DAS_CONVERSAS = ['comunicacao', 'conversas'] as const

/**
 * Prefixo comum das mensagens de **todas** as conversas — é por ele que a invalidação as alcança.
 *
 * `CHAVE_DAS_CONVERSAS` alcança a lista e não as mensagens; este prefixo alcança as mensagens e não
 * a lista. As duas são invalidadas juntas no envio, e a razão está em `useEnviarMensagem`.
 */
export const CHAVE_DAS_MENSAGENS = ['comunicacao', 'mensagens'] as const

/** A chave das mensagens de **uma** conversa. */
export const chaveDasMensagens = (conversaId: string) =>
  [...CHAVE_DAS_MENSAGENS, conversaId] as const

/**
 * De quanto em quanto tempo a tela de conversa relê, em milissegundos.
 *
 * **Esta é a segunda exceção do repositório ao "nenhum relógio", e o fim dela não é um instante.**
 * A decisão do épico é explícita: WebSocket/SSE ficam adiados, e "a mensagem recebida deve aparecer
 * sem recarregar a página". Sem o relógio, o cliente responderia no WhatsApp e o Vendedor
 * continuaria olhando uma tela parada — e ninguém recarrega uma página que parece estar
 * funcionando. É o mesmo modo de falha do pareamento da 8.1, com outro dado.
 *
 * **O fim do relógio do QR é um instante (`expira_em`); o desta é o foco da aba.**
 * `refetchIntervalInBackground` fica no default `false`, então a aba em segundo plano não pergunta
 * nada: o relógio para sozinho quando ninguém está olhando e volta quando a aba volta. É isso que
 * torna aceitável um intervalo que, em tese, nunca acabaria — uma tela de WhatsApp aberta e
 * esquecida numa aba de fundo não gera requisição nenhuma.
 *
 * O intervalo continua sendo função **pura do estado da consulta**, e não um contador guardado num
 * `ref`: ele é reavaliado a cada ciclo do observador, e um contador ali seria incrementado um
 * número de vezes que ninguém consegue prever.
 *
 * **O custo, escrito — porque `INTERVALO_DE_ESPERA_DO_QR` fez questão de escrever o dele.** Com a
 * aba em foco e a tela aberta, são **duas** consultas por ciclo (a lista e a janela de mensagens da
 * conversa aberta), o que dá **30 requisições por minuto por aba** — 15 de cada. E cada uma lê:
 *
 * - `GET /conversas` devolve até **200** linhas, e cada uma traz a prévia da última mensagem
 *   **recortada pelo serviço** em 200 caracteres. O recorte não é detalhe: a coluna `texto` aceita
 *   65.536, e sem ele o pior caso desta chamada seria da ordem de 12 MB — a cada quatro segundos;
 * - `GET /conversas/{id}/mensagens` devolve as últimas **100** mensagens da conversa aberta, sem
 *   recorte (ali o texto inteiro é o produto). Ela relê a janela toda a cada ciclo, e não só o que
 *   veio depois — a razão está em `useMensagens`.
 *
 * Esse é o preço de não ter WebSocket/SSE, que o épico adiou explicitamente. Ele é aceitável porque
 * o relógio **para com o foco**: uma aba de fundo, um computador bloqueado ou uma janela atrás de
 * outra custam zero. O dia em que ele deixar de ser aceitável — mais abas, mais gente, uma lista
 * que cresça — é o dia de trocar o polling pelo canal que o épico adiou, e não o de aumentar o
 * intervalo em silêncio.
 *
 * Quatro segundos é o passo que faz a resposta do cliente parecer imediata sem transformar a tela
 * aberta o dia inteiro num gerador de requisições.
 *
 * **E o binário não entra nessa conta, e essa é a decisão que a 8.4 tomou por causa dela.** As duas
 * respostas acima carregam só o **metadado** da mídia — tipo, nome, tamanho e duração —, e nunca os
 * bytes. A conta explica por quê: um áudio de 300 KB numa conversa aberta seria relido 15 vezes por
 * minuto, e uma lista de 200 conversas cujas últimas mensagens fossem mídia arrastaria dezenas de
 * megabytes por ciclo — para desenhar uma linha que diz `Áudio · 0:22`.
 *
 * Os bytes saem de `GET /midias/{mensagem_id}`, uma requisição **própria**, disparada pelo
 * `<img>`/`<audio>`/`<video>` da tela e não por este módulo. Ela é cacheável (`private, max-age`,
 * `immutable`: o conteúdo de uma mensagem nunca muda), atende faixa de bytes no vídeo, e o
 * navegador a busca **uma vez** por arquivo — enquanto o relógio, ao lado, continua trazendo só
 * texto. É por isso que os elementos de mídia apontam para a URL em vez de receberem um `Blob`
 * baixado pelo SPA: um `Blob` no cache do react-query seria exatamente o custo que este parágrafo
 * existe para evitar.
 */
export const INTERVALO_DA_CONVERSA = 4_000

/**
 * Lista as conversas que a RLS devolve (`200`), da mais recente para a mais antiga.
 *
 * Lista vazia é `[]`, e nunca `404`: quem acabou de conectar o número não tem conversa nenhuma.
 *
 * `403` para quem teve `acessar_whatsapp` negada.
 */
export function listarConversas(): Promise<Conversa[]> {
  return chamar<Conversa[]>('comunicacao', '/conversas')
}

/** O corpo do "Iniciar nova conversa" (13.21b): **um** campo, o número — sem dono e sem Pessoa. */
export type CorpoDaConversaNova = Esquemas['CorpoDaConversaNova']

/**
 * Inicia uma conversa pelo **próprio** número (`201`), ou devolve a que já existia (`200`) — 13.21b,
 * FR-91. A resposta é a mesma linha que `GET /conversas` traria.
 *
 * **O serviço confere no WhatsApp antes de abrir** (`/chat/check`), e toda recusa vem do envelope em
 * PT-BR — mostre-a como veio: `422` (telefone sem dígito, o próprio número, "Este numero nao tem
 * WhatsApp."), `409` (o número desconectado), `429` (a cota do chip) e `503` (o WhatsApp fora ou em
 * dúvida). Não há id de dono aqui, nem de Pessoa: a conversa é sempre de quem está autenticado.
 */
export function iniciarConversa(numero: string): Promise<Conversa> {
  const corpo: CorpoDaConversaNova = { numero }
  return chamar<Conversa>('comunicacao', '/conversas', { method: 'POST', corpo })
}

/**
 * Inicia a conversa e **relê a lista antes de devolver** (13.21b): quem chama abre a conversa pela
 * URL, e a rota só abre o `?conversa=` que a lista tem — sem a releitura, a tela cairia na conversa
 * mais recente com o aviso de link não encontrado.
 *
 * **O único remendo do cache deste módulo**, e ele é estreito: se depois da releitura a conversa
 * ainda não está na lista — a existente antiga, fora da janela das duzentas mais recentes —, a linha
 * que o serviço devolveu entra no topo, para a tela poder abri-la. O relógio da lista a acerta no
 * ciclo seguinte.
 */
export function useIniciarConversa() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: iniciarConversa,
    onSuccess: async (conversa) => {
      await clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_CONVERSAS, exact: true })
      clienteDeConsultas.setQueryData<Conversa[]>(CHAVE_DAS_CONVERSAS, (lista) => {
        if (lista === undefined) {
          return [conversa]
        }
        return lista.some((uma) => uma.id === conversa.id) ? lista : [conversa, ...lista]
      })
    },
  })
}

/**
 * Lê as mensagens de uma conversa (`200`), em ordem cronológica crescente.
 *
 * **Sem `depoisDe`, o que volta são as últimas mensagens** — abrir um chat mostra o fim, e não o
 * começo. **Com `depoisDe`, só o que veio depois daquela mensagem**, e nada novo é `[]`: é o cursor
 * do polling.
 *
 * **O cursor existe no contrato e não tem chamador nesta tela**: `useMensagens` relê a janela
 * inteira a cada ciclo, e a razão está lá. `depoisDe` fica aqui para quem precisar da leitura
 * incremental, e é a suíte do serviço que o exercita.
 *
 * `404` quando a conversa não existe **ou** a RLS não a devolve — os dois casos são a mesma
 * resposta, de propósito. `400` quando o cursor não é mensagem desta conversa: uma lista vazia ali
 * diria "nada novo" a quem, de fato, pediu a coisa errada.
 */
export function lerMensagens(conversaId: string, depoisDe?: string | null): Promise<Mensagem[]> {
  const caminho = `/conversas/${encodeURIComponent(conversaId)}/mensagens`
  // Parâmetro ausente **não** entra na URL: `depois_de=` vazio é `422` (string vazia não é uuid), e
  // produziria uma URL diferente para a mesma consulta.
  if (depoisDe === undefined || depoisDe === null) {
    return chamar<Mensagem[]>('comunicacao', caminho)
  }
  return chamar<Mensagem[]>('comunicacao', `${caminho}?depois_de=${encodeURIComponent(depoisDe)}`)
}

/**
 * Envia um texto por uma conversa (`201`), e devolve a mensagem gravada.
 *
 * **O provedor é chamado antes da escrita**, e por isso a recusa dele não deixa nada no histórico:
 * `503` `whatsapp_indisponivel` significa que a mensagem **não** saiu.
 *
 * `403` quando a conversa entrou pelo número de outra pessoa (FR-71) — o Responsável da Empresa e a
 * gestão a leem, e enviar por ela seria operar chip alheio; a lista o anuncia em `do_meu_numero`
 * (13.11c). `409` quando quem envia
 * não tem número conectado. Mostre a mensagem do serviço como ela veio.
 */
export function enviarMensagem(conversaId: string, texto: string): Promise<Mensagem> {
  return chamar<Mensagem>('comunicacao', `/conversas/${encodeURIComponent(conversaId)}/mensagens`, {
    method: 'POST',
    corpo: { texto },
  })
}

/**
 * Envia um arquivo por uma conversa (`201`), e devolve a mensagem gravada (8.4, FR-41).
 *
 * **`FormData`, e não JSON**: o corpo é `multipart/form-data`, e quem escreve o `Content-Type` com
 * o `boundary` é o navegador — o cliente não escreve nenhum. Um corpo base64 em JSON cresceria um
 * terço por causa da codificação e obrigaria o serviço a manter dois caminhos de escrita.
 *
 * **O provedor é chamado antes da escrita, e o arquivo é apagado se ele recusar**: `503`
 * `whatsapp_indisponivel` significa que a mídia **não** saiu e que nada ficou no servidor.
 *
 * `400` quando o arquivo passa do teto (a mensagem **nomeia** o teto), quando ele está vazio,
 * quando o formato não é aceito para aquele tipo, ou quando a legenda passa do teto do texto.
 * `403` quando a conversa entrou pelo número de outra pessoa (FR-71); `409` quando quem envia não
 * tem número conectado. Mostre a mensagem do serviço como ela veio.
 */
export function enviarMidia(conversaId: string, midia: MidiaAEnviar): Promise<Mensagem> {
  const formulario = new FormData()
  // O terceiro argumento é o nome do arquivo. Ele é dado quando existe (o anexo escolhido no
  // disco), e omitido quando não (o áudio gravado): o serviço grava a mídia de áudio **sem** nome,
  // porque uma mensagem de voz não é um arquivo que alguém batizou.
  if (midia.nome === undefined) {
    formulario.append('arquivo', midia.arquivo)
  } else {
    formulario.append('arquivo', midia.arquivo, midia.nome)
  }
  formulario.append('tipo', midia.tipo)
  if (midia.legenda !== undefined && midia.legenda !== '') {
    formulario.append('legenda', midia.legenda)
  }
  if (midia.duracaoEmSegundos !== undefined) {
    formulario.append('duracao_em_segundos', String(midia.duracaoEmSegundos))
  }
  return chamar<Mensagem>('comunicacao', `/conversas/${encodeURIComponent(conversaId)}/midias`, {
    method: 'POST',
    formulario,
  })
}

/**
 * A lista de conversas, relida sozinha enquanto a aba está em foco.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada aqui é o `403` de quem teve a
 * permissão negada — que não muda por insistir — e o `401` de sessão expirada, que o cliente já
 * renova sozinho antes de deixar o erro subir.
 *
 * O `refetchInterval` é uma constante, e o que o desliga é o **foco** da aba
 * (`refetchIntervalInBackground` no default `false`): a justificativa inteira está em
 * `INTERVALO_DA_CONVERSA`.
 */
export function useConversas() {
  return useQuery({
    queryKey: CHAVE_DAS_CONVERSAS,
    queryFn: listarConversas,
    retry: false,
    refetchInterval: INTERVALO_DA_CONVERSA,
    /*
     * **A volta do foco relê na hora (13.8).** O padrão do SPA é não reler no foco, e aqui ele
     * custaria a contagem de não lidas: a conversa aberta por clique que recebeu mensagem com a aba
     * oculta precisa ser vista — e marcada lida — quando a pessoa volta, e não até 4 s depois.
     * `'always'`, e não `true`: o `staleTime` de 30 s do SPA faria `true` não reler nada.
     */
    refetchOnWindowFocus: 'always',
  })
}

/**
 * As mensagens de uma conversa, relidas sozinhas enquanto a aba está em foco.
 *
 * **Ela relê a conversa inteira a cada ciclo, e não só o que veio depois.** O cursor existe no
 * serviço e é usado por quem precisar dele; aqui a releitura completa é o que mantém o cache
 * **consistente sem remendo**: uma lista incremental exigiria concatenar no cache a cada resposta,
 * e um ciclo perdido (a aba em segundo plano, a rede caindo) deixaria um buraco silencioso no meio
 * da conversa. O lote tem teto no serviço, e o que a tela mostra são as últimas mensagens.
 *
 * `enabled` é `false` quando não há conversa escolhida — não há o que ler, e uma consulta desligada
 * não liga relógio nenhum.
 */
export function useMensagens(conversaId: string | undefined) {
  return useQuery({
    queryKey: chaveDasMensagens(conversaId ?? ''),
    queryFn: () => lerMensagens(conversaId as string),
    enabled: conversaId !== undefined,
    retry: false,
    refetchInterval: INTERVALO_DA_CONVERSA,
  })
}

/**
 * Envia e relê — a lista **e** as mensagens daquela conversa.
 *
 * **As duas chaves, e não uma.** A mensagem entra no chat (as mensagens) e muda a ordem e a prévia
 * da lista (`ultima_mensagem_em` avança do lado do serviço): invalidar só uma delas deixaria a
 * outra mostrando o estado de antes até o próximo ciclo do relógio.
 *
 * **`onSuccess`, e não `onSettled`.** O desfecho de erro aqui — `403`, `409`, `503` — diz que
 * **nada** mudou no serviço, e reler seria uma requisição que já se sabe que devolveria o mesmo
 * corpo. O relógio da tela cobre a janela em que a recusa for de permissão retirada.
 */
export function useEnviarMensagem(conversaId: string | undefined) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (texto: string) => enviarMensagem(conversaId as string, texto),
    onSuccess: async () => {
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_CONVERSAS }),
        clienteDeConsultas.invalidateQueries({ queryKey: chaveDasMensagens(conversaId ?? '') }),
      ])
    },
  })
}

/**
 * Envia mídia e relê — as **duas** chaves, exatamente como o envio de texto (8.4).
 *
 * As duas pela mesma razão de `useEnviarMensagem`: a mensagem entra no chat (as mensagens) e muda a
 * ordem e a prévia da lista (`ultima_mensagem_em` avança do lado do serviço). Invalidar só uma
 * deixaria a outra mostrando o estado de antes até o próximo ciclo do relógio — e aqui a prévia é
 * justamente o rótulo novo (`📄 Proposta.pdf · 1,2 MB`).
 *
 * **`onSuccess`, e não `onSettled`**, também pela mesma razão: as recusas (`400`, `403`, `409`,
 * `503`) dizem que **nada** mudou no serviço — nem linha, nem arquivo.
 *
 * Ele é uma mutação separada da de texto, e não a mesma com um parâmetro opcional: as duas mandam
 * corpos de formatos diferentes para rotas diferentes, e uni-las produziria uma função cujo tipo de
 * entrada é uma união que nenhum chamador usa inteira.
 */
export function useEnviarMidia(conversaId: string | undefined) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (midia: MidiaAEnviar) => enviarMidia(conversaId as string, midia),
    onSuccess: async () => {
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_CONVERSAS }),
        clienteDeConsultas.invalidateQueries({ queryKey: chaveDasMensagens(conversaId ?? '') }),
      ])
    },
  })
}

// --- A leitura da conversa (13.8, FR-87) -----------------------------------------------------

/** O total do menu lateral, como o serviço o devolve. */
export type TotalDeNaoLidas = Esquemas['RespostaDasNaoLidas']

/**
 * Chave do total de não lidas do menu — **fora** do prefixo `CHAVE_DAS_CONVERSAS`, de propósito: a
 * marca otimista mexe na lista por `setQueryData` exato, e a invalidação do envio alcança a lista
 * pelo prefixo; com o total debaixo do mesmo prefixo, os dois o atingiriam por engano.
 */
export const CHAVE_DO_TOTAL_DE_NAO_LIDAS = ['comunicacao', 'nao-lidas'] as const

/**
 * De quanto em quanto o shell relê o total, com a aba em foco (D-16): trinta segundos — o mesmo
 * passo da situação do número. Na tela de WhatsApp a lista (4 s) invalida o total quando os
 * contadores mudam, e marcar invalida na hora.
 */
export const INTERVALO_DO_TOTAL_DE_NAO_LIDAS = 30_000

/** Acima disto o contador de não lidas escreve "99+" (13.8, D-10) — na linha e no menu. */
export const TETO_DO_CONTADOR = 99

/**
 * Marca a conversa lida (`true`) ou não lida (`false`) — `204` sem corpo.
 *
 * Só quem **lê** a conversa marca (o dono do número ou o Responsável da Empresa dela); os demais
 * recebem `404`. Quando quem marca é o dono, o serviço reflete no WhatsApp do celular depois de
 * responder — a tela não espera isso, e a falha lá não volta para cá.
 */
export function marcarLeitura(conversaId: string, lida: boolean): Promise<void> {
  return chamar<void>('comunicacao', `/conversas/${encodeURIComponent(conversaId)}/leitura`, {
    method: 'PUT',
    corpo: { lida },
  })
}

/** O total de não lidas de quem pede, para o menu lateral (`200`). */
export function lerTotalDeNaoLidas(): Promise<TotalDeNaoLidas> {
  return chamar<TotalDeNaoLidas>('comunicacao', '/conversas/nao-lidas')
}

/**
 * O total do menu, relido a cada 30 s com a aba em foco e na volta do foco.
 *
 * `habilitada` é a permissão `acessar_whatsapp` da sessão: quem não a tem não pergunta (a rota
 * responderia `403`). `retry: false`: a recusa (o `403`, o `404` do serviço anterior à 13.8 na
 * janela do deploy) não muda por insistir, e o menu simplesmente não mostra número.
 */
export function useTotalDeNaoLidas({ habilitada }: { habilitada: boolean }) {
  return useQuery({
    queryKey: CHAVE_DO_TOTAL_DE_NAO_LIDAS,
    queryFn: lerTotalDeNaoLidas,
    enabled: habilitada,
    retry: false,
    refetchInterval: INTERVALO_DO_TOTAL_DE_NAO_LIDAS,
    // `'always'`: com o `staleTime` de 30 s do SPA, `true` não releria na volta do foco.
    refetchOnWindowFocus: 'always',
  })
}

/**
 * Marca lida ou não lida, com a **marca otimista** na lista.
 *
 * `onMutate` cancela a releitura em voo da lista (senão ela, lida antes do commit, derrubaria o
 * otimista) e ajusta a linha por `setQueryData` **exato**; `onError` devolve o que havia; o fim
 * relê a lista e o total. `retry: false`: a recusa (`404` de quem deixou de ler) não muda por
 * insistir.
 */
export function useMarcarLeitura() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: ({ conversaId, lida }: { conversaId: string; lida: boolean }) =>
      marcarLeitura(conversaId, lida),
    onMutate: async ({ conversaId, lida }) => {
      await clienteDeConsultas.cancelQueries({ queryKey: CHAVE_DAS_CONVERSAS, exact: true })
      const linhaDeAntes = clienteDeConsultas
        .getQueryData<Conversa[]>(CHAVE_DAS_CONVERSAS)
        ?.find((conversa) => conversa.id === conversaId)
      clienteDeConsultas.setQueryData<Conversa[]>(CHAVE_DAS_CONVERSAS, (lista) =>
        lista?.map((conversa) =>
          conversa.id === conversaId
            ? lida
              ? { ...conversa, nao_lidas: 0, marcada_nao_lida: false }
              : { ...conversa, marcada_nao_lida: true }
            : conversa,
        ),
      )
      return { linhaDeAntes }
    },
    /* Desfaz **só a linha** marcada: outra marca em voo, ou uma releitura que chegou no meio,
       não voltam junto. */
    onError: (_erro, { conversaId }, contexto) => {
      const linhaDeAntes = contexto?.linhaDeAntes
      if (linhaDeAntes !== undefined) {
        clienteDeConsultas.setQueryData<Conversa[]>(CHAVE_DAS_CONVERSAS, (lista) =>
          lista?.map((conversa) => (conversa.id === conversaId ? linhaDeAntes : conversa)),
        )
      }
    },
    onSettled: async () => {
      await Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_CONVERSAS, exact: true }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_TOTAL_DE_NAO_LIDAS }),
      ])
    },
  })
}

// --- A reação (8.6, FR-76) ------------------------------------------------------------------

/**
 * Reage a uma mensagem com um emoji do conjunto fixo (`200`), e devolve a mensagem atualizada.
 *
 * **O provedor é chamado antes da escrita**: `503` `whatsapp_indisponivel` significa que a reação
 * **não** saiu e nada mudou. Reagir de novo substitui — uma reação por lado, como no WhatsApp.
 *
 * `422` nomeando o conjunto aceito para emoji fora dele; `409` para número desconectado e para
 * mensagem sem identificador no provedor; `403` quando a conversa entrou pelo número de outra
 * pessoa (FR-71 — reagir é falar pelo chip); `404` para conversa ou mensagem que a RLS não
 * devolve. Mostre a mensagem do serviço como ela veio.
 */
export function reagir(conversaId: string, mensagemId: string, emoji: string): Promise<Mensagem> {
  const caminho =
    `/conversas/${encodeURIComponent(conversaId)}` +
    `/mensagens/${encodeURIComponent(mensagemId)}/reacao`
  return chamar<Mensagem>('comunicacao', caminho, { method: 'PUT', corpo: { emoji } })
}

/**
 * Remove a própria reação daquela mensagem (`200`) — `text` vazio no provedor, coluna anulada.
 *
 * **Repetir é idempotente**: a segunda remoção responde o mesmo `200` com a mesma mensagem.
 */
export function removerReacao(conversaId: string, mensagemId: string): Promise<Mensagem> {
  const caminho =
    `/conversas/${encodeURIComponent(conversaId)}` +
    `/mensagens/${encodeURIComponent(mensagemId)}/reacao`
  return chamar<Mensagem>('comunicacao', caminho, { method: 'DELETE' })
}

/**
 * Reage (ou remove, quando `emoji` é `null`) e relê **as mensagens** — e só elas.
 *
 * **Uma chave, e não duas, ao contrário do envio** — e a diferença é o que a reação não faz: ela
 * não cria mensagem, não avança `ultima_mensagem_em` e não muda a prévia, então invalidar a lista
 * seria uma requisição que já se sabe que devolveria o mesmo corpo. O chip aparece no balão pela
 * releitura das mensagens.
 *
 * **Nenhum relógio novo**: a reação que o cliente manda chega pelo polling que já existe
 * (`useMensagens`), porque ela é coluna da mensagem — não há segunda consulta a ligar.
 *
 * **`onSuccess`, e não `onSettled`**, pela razão do envio: o desfecho de erro (`403`, `409`,
 * `422`, `503`) diz que **nada** mudou no serviço.
 */
export function useReagir(conversaId: string | undefined) {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ mensagemId, emoji }: { mensagemId: string; emoji: string | null }) => {
      /*
       * Guarda explícita, e não um `as string`: sem conversa aberta não há balão com gesto na
       * tela, então este ramo é inalcançável pelo uso normal — mas um chamador futuro receberia
       * um erro que diz a causa, em vez de um `PUT /conversas/undefined/...` que vira `422` do
       * serviço.
       */
      if (conversaId === undefined) {
        return Promise.reject(new Error('Não há conversa aberta para reagir.'))
      }
      return emoji === null
        ? removerReacao(conversaId, mensagemId)
        : reagir(conversaId, mensagemId, emoji)
    },
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: chaveDasMensagens(conversaId ?? '') })
    },
  })
}


// --- O e-mail arquivado por cópia (8.5, FR-42, FR-31, AD-25) --------------------------------

/**
 * Um e-mail arquivado, como o serviço o devolve.
 *
 * **Sem `id_da_mensagem`**: ele é detalhe da idempotência da varredura, e a tela não sabe usá-lo —
 * o mesmo critério que deixou `id_no_provedor` fora de `Mensagem` na 8.3.
 *
 * **`corpo` é o texto, e pode vir truncado com marca.** Não há HTML e não há anexo: o binário
 * continua no e-mail original, e o CRM não o guarda (o recorte da história).
 */
export type EmailArquivado = Esquemas['RespostaDoEmail']

/**
 * O envelope da lista, como o serviço o devolve — `{ itens: [...] }`.
 *
 * Ele é apelido do schema gerado, e **não** um `{ itens: EmailArquivado[] }` digitado à mão (AD-16,
 * a regra escrita no cabeçalho deste módulo): digitado, ele seria uma segunda cópia do contrato
 * nesta camada, e o dia em que a resposta ganhasse um `total` ou um cursor esta cópia continuaria
 * verde dizendo que ele não existe. O envelope existe justamente para poder crescer (5.3).
 */
export type ListaDeEmails = Esquemas['RespostaDaListaDeEmails']

/**
 * Chave de cache do histórico de e-mail de **um** Negócio.
 *
 * Ela leva o `negocioId` dentro — ao contrário de `CHAVE_DAS_CONVERSAS` —, porque o recurso é de um
 * Negócio e não de quem está autenticado: duas fichas abertas em abas diferentes leem históricos
 * diferentes, e uma chave sem o id faria a segunda mostrar o da primeira.
 */
export const CHAVE_DOS_EMAILS = ['comunicacao', 'emails'] as const

/** A chave do histórico de e-mail daquele Negócio. */
export const chaveDosEmailsDoNegocio = (negocioId: string) =>
  [...CHAVE_DOS_EMAILS, negocioId] as const

/**
 * Lê o histórico de e-mail arquivado de um Negócio (`200`), do mais recente para o mais antigo.
 *
 * **`200` com `{"itens": []}` tem duas causas, e a rota não as distingue de propósito**: o Negócio
 * não tem e-mail arquivado, ou a matriz de visibilidade (FR-31) não o entrega a quem pediu. Um
 * `404` na segunda contaria a existência do Negócio a quem a ficha já esconde — e esta rota não é a
 * dona do Negócio.
 *
 * `403` para quem teve `acessar_whatsapp` negada — a mesma permissão das leituras de conversa, e
 * nenhuma nova (catálogo fechado do FR-61-A).
 */
export function lerEmailsDoNegocio(negocioId: string): Promise<ListaDeEmails> {
  return chamar<ListaDeEmails>('comunicacao', `/negocios/${encodeURIComponent(negocioId)}/emails`)
}

/**
 * O histórico de e-mail de um Negócio — `useQuery` simples, **sem relógio**.
 *
 * **A ausência do relógio é a decisão, e ela é o oposto da de `useConversas`.** Todo polling deste
 * repositório precisa de um fim escrito (o `expira_em` do QR na 8.1, o foco da aba na 8.3), e aqui
 * não há um: a ficha do Negócio é uma tela de leitura que fica aberta, e o e-mail chega por uma
 * varredura de **um minuto** — um relógio de quatro segundos faria quinze requisições por minuto
 * para trazer o mesmo corpo quinze vezes. Quem quiser o mais novo recarrega a ficha, que é o gesto
 * que a tela toda já pede.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada aqui é o `403` de quem teve a
 * permissão negada — que não muda por insistir — e o `401` de sessão expirada, que o cliente já
 * renova sozinho antes de deixar o erro subir.
 *
 * **`habilitada` fecha a porta antes da rede**, no molde de `useOrcamentosDoNegocio`: um hook roda
 * antes de qualquer `return null` do componente, e sem esta chave o cartão pediria o histórico —
 * para receber o `403` esperado — em toda ficha aberta por quem teve `acessar_whatsapp` negada.
 */
export function useEmailsDoNegocio(
  negocioId: string,
  { habilitada = true }: { habilitada?: boolean } = {},
) {
  return useQuery({
    queryKey: chaveDosEmailsDoNegocio(negocioId),
    queryFn: () => lerEmailsDoNegocio(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/* --- A prontidão de go-live (história 9.2) ---------------------------------------- */

/**
 * Quem já conectou o próprio número — **só os ids**, como o serviço os devolve.
 *
 * **Sem telefone, sem nome de instância e sem situação**, e a ausência é o requisito: a policy do
 * FR-71 não tem ramo de gestão, e a pergunta que esta rota responde é outra — *quem já conectou*,
 * e não *com qual número*. Quem cruza estes ids com a equipe é o SPA, com a lista de usuários da
 * `identidade` (AD-25).
 */
export type ProntidaoDoWhatsapp = Esquemas['RespostaDeProntidaoDoWhatsapp']

/**
 * Chave de cache da prontidão do WhatsApp — **própria**, e não a do próprio número.
 *
 * São dois recursos diferentes: `GET /meu-numero` responde o estado de quem está olhando, e
 * `GET /prontidao` responde quem, na equipe inteira, já conectou. Compartilhar a chave faria o
 * relógio do pareamento (`useMeuNumero`) refazer, a cada quatro segundos, uma leitura só de Admin
 * — e faria o cartão de prontidão da Configuração ser invalidado por um QR que ninguém ali está lendo.
 */
export const CHAVE_DA_PRONTIDAO = ['comunicacao', 'prontidao'] as const

/**
 * Lê quem já conectou o próprio número (`200`).
 *
 * Só Admin: Vendedor, SDR e **Gestor comercial** recebem `403` — a leitura inclusive, e o Gestor
 * está fora pela mesma razão que ele está fora da policy do número (FR-71). Lista vazia é a
 * resposta certa numa instalação nova, e nunca um `404`.
 */
export function lerProntidao(): Promise<ProntidaoDoWhatsapp> {
  return chamar<ProntidaoDoWhatsapp>('comunicacao', '/prontidao')
}

/**
 * A prontidão do WhatsApp, para o cartão de go-live (9.2).
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada é o `403` de quem não é Admin
 * — que não muda por insistir — e o `401` de sessão expirada, que o cliente já renova sozinho.
 *
 * **`habilitada` fecha a porta antes da rede, e aqui ela não é otimização.** O cartão devolve
 * `null` para quem não é Admin, e um hook roda **antes** de qualquer `return null` do componente:
 * sem esta chave, todo Vendedor que abrisse `/configuracao` dispararia uma chamada a uma rota que
 * já se sabe que vai recusar.
 *
 * **O cartão compõe quatro fontes e fecha três — esta, a lista de usuários e a prontidão do
 * backup.** São as três cujas rotas exigem Admin do lado do serviço. A quarta (as listas de
 * domínio) fica aberta de propósito: a rota atende qualquer perfil autenticado, e o cartão das
 * listas já a pede para todo mundo, com a mesma chave de cache. (A régua saiu na 11.2.)
 */
export function useProntidaoDoWhatsapp({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DA_PRONTIDAO,
    queryFn: lerProntidao,
    enabled: habilitada,
    retry: false,
  })
}

/* --- A situacao do WhatsApp da equipe (historia 11.1) ------------------------------ */

/**
 * Uma linha de `GET /numeros`: de quem e o numero, a situacao e o numero. Nunca o nome da
 * instancia nem token. Usuario que nunca pediu pareamento nao tem linha — a tela mostra "—".
 */
export type SituacaoDoWhatsapp = Esquemas['RespostaDaSituacaoDaEquipe']

/** Chave de cache da situacao da equipe. */
export const CHAVE_DA_SITUACAO_DA_EQUIPE = ['comunicacao', 'numeros'] as const

/**
 * Le a situacao do WhatsApp de cada usuario (`200`). Guarda `gerir_usuarios` **ou**
 * `gerir_funis_e_etapas` (13.21a1); os demais recebem `403`. Pelo `gerir_usuarios`, a forma completa;
 * so pelo `gerir_funis_e_etapas`, so as linhas com `tem_numero`, com `numero` e `situacao` nulos — e
 * a Oportunidade automatica (13.21a2) le dela so `usuario_id` e `tem_numero`.
 */
export function listarSituacaoDoWhatsapp(): Promise<SituacaoDoWhatsapp[]> {
  return chamar<SituacaoDoWhatsapp[]>('comunicacao', '/numeros')
}

/** A situacao da equipe, para a coluna da tela Usuarios. */
export function useSituacaoDoWhatsapp({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DA_SITUACAO_DA_EQUIPE,
    queryFn: listarSituacaoDoWhatsapp,
    enabled: habilitada,
    retry: false,
  })
}

/* --- As credenciais de integração do `comunicacao` (história 11.2, AD-30, FR-82) ---------- */

/**
 * O que `GET /integracoes` responde: o bloco de credenciais **ou `null`**.
 *
 * O bloco — token de admin do uazapi, conta IMAP, estado derivado do SMTP e destinatários do aviso
 * de RAT — só vem para quem tem `editar_credenciais_de_integracao`; para os demais perfis ele
 * chega `null`. Do segredo vêm, no máximo, os **últimos caracteres**: nunca o valor inteiro.
 */
export type IntegracoesDaComunicacao = Esquemas['RespostaDeIntegracoesDaComunicacao']

/** O bloco de credenciais, quando ele vem. */
export type CredenciaisDaComunicacao = Esquemas['CredenciaisDaComunicacao']

/** O estado de uma credencial: configurada ou não, os últimos caracteres e quando mudou. */
export type EstadoDaCredencial = Esquemas['EstadoDaCredencial']

/** A conta IMAP como ela é mostrada: parâmetros em claro e a senha só como estado. */
export type EstadoDaContaImap = Esquemas['EstadoDaContaImap']

/** O que a tela envia para trocar a conta IMAP. `senha` ausente ou `null` mantém a atual. */
export type ContaImap = Esquemas['CorpoDaContaImap']

/** Chave de cache das credenciais do `comunicacao`. */
export const CHAVE_DAS_INTEGRACOES_DA_COMUNICACAO = ['comunicacao', 'integracoes'] as const

/** Lê o estado das credenciais (`200`; `credenciais: null` para quem não pode vê-las). */
export function lerIntegracoesDaComunicacao(): Promise<IntegracoesDaComunicacao> {
  return chamar<IntegracoesDaComunicacao>('comunicacao', '/integracoes')
}

/** Troca o token de admin do uazapi (`200`). O adaptador o lê a cada uso — sem reinício. */
export function trocarTokenDoUazapi(tokenDeAdmin: string): Promise<IntegracoesDaComunicacao> {
  return chamar<IntegracoesDaComunicacao>('comunicacao', '/integracoes/uazapi', {
    method: 'PUT',
    corpo: { token_de_admin: tokenDeAdmin } satisfies Esquemas['CorpoDoTokenDoUazapi'],
  })
}

/**
 * Troca a conta IMAP (`200`). Os campos vão **um a um**: o corpo do serviço é fechado
 * (`extra="forbid"`), e a senha só vai quando a pessoa digitou uma nova — ou quando pediu para
 * removê-la, com `""` (o serviço apaga a senha gravada; `null`/ausente a mantém).
 */
export function trocarContaImap(conta: ContaImap): Promise<IntegracoesDaComunicacao> {
  const corpo: ContaImap = {
    servidor: conta.servidor,
    porta: conta.porta,
    usuario: conta.usuario,
    seguranca: conta.seguranca,
    pasta: conta.pasta,
    ...(conta.senha === undefined || conta.senha === null ? {} : { senha: conta.senha }),
  }
  return chamar<IntegracoesDaComunicacao>('comunicacao', '/integracoes/imap', {
    method: 'PUT',
    corpo,
  })
}

/** Troca os destinatários do aviso de RAT (`200`; `422` para e-mail malformado). */
export function trocarDestinatariosDoAvisoDeRat(
  destinatarios: readonly string[],
): Promise<IntegracoesDaComunicacao> {
  return chamar<IntegracoesDaComunicacao>(
    'comunicacao',
    '/integracoes/destinatarios-do-aviso-de-rat',
    {
      method: 'PUT',
      corpo: { destinatarios: [...destinatarios] } satisfies Esquemas['CorpoDosDestinatarios'],
    },
  )
}

/**
 * As credenciais do `comunicacao`. `habilitada` fecha a porta antes da rede para quem não tem a
 * permissão de credenciais — a resposta viria `null` de qualquer forma.
 */
export function useIntegracoesDaComunicacao({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DAS_INTEGRACOES_DA_COMUNICACAO,
    queryFn: lerIntegracoesDaComunicacao,
    enabled: habilitada,
    retry: false,
  })
}

/** Qual das três escritas, e o valor. */
export type TrocaDeCredencialDaComunicacao =
  | { credencial: 'uazapi'; tokenDeAdmin: string }
  | { credencial: 'imap'; conta: ContaImap }
  | { credencial: 'destinatarios'; destinatarios: readonly string[] }

/**
 * Troca uma credencial e **grava a resposta no cache**: ela já é o estado completo depois da
 * troca — reler seria uma ida a mais para a mesma resposta.
 */
export function useTrocarCredencialDaComunicacao() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (troca: TrocaDeCredencialDaComunicacao) => {
      if (troca.credencial === 'uazapi') {
        return trocarTokenDoUazapi(troca.tokenDeAdmin)
      }
      if (troca.credencial === 'imap') {
        return trocarContaImap(troca.conta)
      }
      return trocarDestinatariosDoAvisoDeRat(troca.destinatarios)
    },
    onSuccess: (resposta) => {
      clienteDeConsultas.setQueryData(CHAVE_DAS_INTEGRACOES_DA_COMUNICACAO, resposta)
    },
  })
}
